import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import Quiz from '@/components/application/quiz/Quiz'
import Timer from '@/components/application/books/TimeElapsed'
import { pb } from '@/lib/pocketbase'
import { queryClient } from '@/lib/query-client'
import { Collections, BooksPhaseOptions, type BooksResponse } from '@/types/pocketbase-types'
import type { Question, Answer } from '@/types/extended-types'

interface IBookQuizProps {
  summaryId?: string
  bookId?: string
  quizQuestions?: Question[]
  bookSlug: string
  isProcessing?: boolean
  bookPhase?: BooksPhaseOptions
  quizTaken?: number
}

const BookQuizContainer = ({
  bookId,
  quizQuestions,
  summaryId,
  bookSlug,
  isProcessing,
  bookPhase,
  quizTaken = 0
}: IBookQuizProps) => {
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [currentPhase, setCurrentPhase] = useState<BooksPhaseOptions | null>(bookPhase || null)
  const [quiz, setQuiz] = useState(quizQuestions)
  const [bookSummary, setBookSummary] = useState(summaryId)
  const [hasReceivedProcessedSignal, setHasReceivedProcessedSignal] = useState(false)
  const [isFetchingQuestions, setIsFetchingQuestions] = useState(false)
  const [quizStarted, setQuizStarted] = useState(false)
  const [progressFlags, setProgressFlags] = useState({
    search_completed: false,
    metadata_downloaded: false,
    cover_downloaded: false,
    epub_downloaded: false,
    summarized: false,
    questions_generated: false,
  })

  // Helper to get phase badge styling (completed = green, active = amber pulse, pending = gray)
  const getPhaseStyle = (isCompleted: boolean, isActive: boolean) => {
    if (isCompleted) return 'bg-green-200 text-green-800';
    if (isActive) return 'bg-amber-200 text-amber-800 animate-pulse';
    return 'bg-gray-200 text-gray-600';
  };

  // Determine which phase is currently active (first incomplete in sequence)
  const getActivePhase = () => {
    if (!progressFlags.search_completed) return 'search';
    if (!progressFlags.metadata_downloaded) return 'metadata';
    if (!progressFlags.cover_downloaded) return 'cover';
    if (!progressFlags.epub_downloaded) return 'epub';
    if (!progressFlags.summarized) return 'summary';
    if (!progressFlags.questions_generated) return 'questions';
    return null; // All complete
  };

  const activePhase = getActivePhase();

  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  
  const handleQuizComplete = async (answers: Answer[], timeSpentSeconds: number) => {
    try {
      // Use bookSummary state or fallback to summaryId prop
      const summaryIdToSubmit = bookSummary || summaryId

      // Validate that we have a summary ID
      if (!summaryIdToSubmit || summaryIdToSubmit === '') {
        throw new Error('Book summary ID is missing. Please refresh and try again.')
      }

      const requestBody = {
        bookSummaryId: summaryIdToSubmit,
        answers,
        timeSpent: timeSpentSeconds
      }

      
      const response = await fetch(`${pb.baseUrl}/api/quiz/book/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${pb.authStore.token}`,
        },
        body: JSON.stringify(requestBody),
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Quiz submission failed')
      }

      // Invalidate user activities to update profile quiz history
      await queryClient.invalidateQueries({ queryKey: ['userActivities'] })
      enqueueSnackbar('Quiz Submitted successfully', { variant: 'success', autoHideDuration: 2000 })
      navigate({ to: '/books/quiz-results/$slug', params: { slug: bookSlug } })
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('Quiz submission error:', error);
      }
      enqueueSnackbar('Something went wrong. Please try again.', { variant: 'error' })
    }
  }
  
  const handleCancel = () => {
    window.location.href = '/books/search'
  }
  
  // Update quiz when questions prop changes
  useEffect(() => {
    if (quizQuestions && quizQuestions.length > 0) {
      setQuiz(quizQuestions)
      setError(false)
    }
  }, [quizQuestions])
  
  // Update bookSummary when summaryId prop changes
  useEffect(() => {
    if (summaryId) {
      setBookSummary(summaryId)
    }
  }, [summaryId])

  // Update current phase when prop changes
  useEffect(() => {
    if (bookPhase) {
      setCurrentPhase(bookPhase)
    }
  }, [bookPhase])
  
  // Set up real-time subscription if quiz questions are not ready
  useEffect(() => {
    // If we have questions or already have quiz state, don't set up subscription
    if (quizQuestions || quiz) return
    
    // If we're processing or about to process, never show error
    if (isProcessing || !bookId) {
      setError(false)
    }
    
    let unsubscribe: (() => void) | null = null
    let isSubscribed = true
    
    const setupSubscription = async () => {
      try {
        // If we don't have a bookId yet, we need to wait
        if (!bookId) {
          console.log('Unified BookQuiz: No bookId available, waiting...')
          return
        }

        console.log('Unified BookQuiz: Setting up subscription for book:', bookId)
        
        // Set up subscription directly with bookId
        setupBookSubscription(bookId)
      } catch (error) {
        console.error('Unified BookQuiz subscription error:', error)
        setError(true)
      }
    }

    const setupBookSubscription = async (id: string) => {
      try {
        unsubscribe = await pb.collection(Collections.Books).subscribe(id, async (data) => {
          if (!isSubscribed) return
          
          const book = data.record as BooksResponse
          const {
            phase,
            search_completed,
            metadata_downloaded,
            cover_downloaded,
            epub_downloaded,
            summarized,
            questions_generated,
            error_phase,
            error_message
          } = book

          setProgressFlags({
            search_completed: search_completed || false,
            metadata_downloaded: metadata_downloaded || false,
            cover_downloaded: cover_downloaded || false,
            epub_downloaded: epub_downloaded || false,
            summarized: summarized || false,
            questions_generated: questions_generated || false,
          })

          setCurrentPhase(phase)
          
          // Handle phase transitions
          switch (phase) {
            case BooksPhaseOptions.processed:
              if (!hasReceivedProcessedSignal) {
                console.log('Unified BookQuiz: Book processed, fetching questions...')
                setHasReceivedProcessedSignal(true)
                setIsFetchingQuestions(true)
                
                try {
                  // Fetch summary and questions
                  const summary = await pb.collection(Collections.BookSummaries)
                    .getFirstListItem(`book="${id}"`)
                  
                  setBookSummary(summary.id)
                  
                  const questionsResponse = await pb.collection(Collections.BookQuestions).getList(1, 50, {
                    filter: `book_summary="${summary.id}"`,
                    sort: '@random',
                  })
                  
                  // Format questions
                  const loQuestions = questionsResponse.items.filter((q: any) => q.type === 'LO').slice(0, 0)
                  const mcqQuestions = questionsResponse.items.filter((q: any) => q.type === 'MCQ').slice(0, 15)
                  const limitedQuestions = [...loQuestions, ...mcqQuestions]
                  
                  const formattedQuestions: Question[] = limitedQuestions.map((q: any) => ({
                    id: q.id,
                    type: q.type, // Keep original 'MCQ' or 'LO' types
                    question: q.question,
                    options: q.type === 'MCQ' ? (q.choices as string[] || []) : [],
                    correctAnswer: q.type === 'MCQ' ? q.answer || '' : '',
                    openEndedAnswers: q.type === 'LO' ? (q.lo_answers as string[] || []) : [],
                  }))
                  
                  console.log('Unified BookQuiz: Questions fetched:', formattedQuestions.length)
                  setQuiz(formattedQuestions)
                  setIsFetchingQuestions(false)
                  setError(false)
                  
                } catch (fetchError) {
                  console.error('Unified BookQuiz: Error fetching questions:', fetchError)
                  setError(true)
                  setIsFetchingQuestions(false)
                }
              }
              break
              
            case BooksPhaseOptions.error:
              console.log('Unified BookQuiz: Book processing error:', error_phase, error_message)
              setError(true)
              setErrorMessage(error_message || null)
              break
          }
        })
      } catch (subscriptionError) {
        console.error('Unified BookQuiz: Failed to set up subscription:', subscriptionError)
        setError(true)
      }
    }

    setupSubscription()

    // Cleanup function
    return () => {
      isSubscribed = false
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [bookId, quizQuestions, quiz, hasReceivedProcessedSignal])

  // Get progress message based on current phase and flags
  const getProgressMessage = () => {
    if (currentPhase === BooksPhaseOptions.error) return 'Something went wrong. Please try again.'
    if (currentPhase === BooksPhaseOptions.processed && quiz?.length && !quizStarted) return 'Ready to start!'
    if (currentPhase === BooksPhaseOptions.processed && isFetchingQuestions) return 'Almost there...'
    if (currentPhase === BooksPhaseOptions.processing) return 'Please wait, this may take up to 2 minutes...'
    if (currentPhase === BooksPhaseOptions.downloaded) return 'Getting things ready...'
    if (currentPhase === BooksPhaseOptions.downloading) return 'Please wait a moment...'
    if (currentPhase === BooksPhaseOptions.searched) return 'Just a moment...'
    if (currentPhase === BooksPhaseOptions.searching) return 'Looking for your book...'
    return 'Getting started...'
  }

  // Render quiz if we have questions, are ready, and user has started quiz
  if (quiz && quiz.length > 0 && !isProcessing && currentPhase === BooksPhaseOptions.processed && quizStarted) {
    return (
      <div className="flex-1 flex flex-col">
        {/* Timer at the top when quiz is active */}
        <div className="mb-4">
          <Timer />
        </div>
        
        <Quiz
          questions={quiz}
          onComplete={handleQuizComplete}
          onCancel={handleCancel}
        />
      </div>
    )
  }
  
  // Render "Start Quiz" button if questions are ready but quiz hasn't started
  if (quiz && quiz.length > 0 && !isProcessing && currentPhase === BooksPhaseOptions.processed && !quizStarted) {
    // Check if user has exhausted their attempts (max 2)
    const hasTriesLeft = quizTaken < 2

    return (
      <div className="flex-1 bg-neutral-100 rounded-[15px] p-7 flex flex-col items-center justify-center gap-y-7">
        <div className="text-center">
          <h3 className="text-[24px] font-bold text-green-600 mb-4">Quiz Ready!</h3>
          <p className="text-neutral-600 mb-2">
            Your book has been processed and {quiz.length} questions are ready.
          </p>
          <p className="text-neutral-500 text-sm mb-6">
            You can take the quiz only 2 times - {quizTaken} / 2
          </p>

          {hasTriesLeft ? (
            <button
              onClick={() => setQuizStarted(true)}
              className="bg-action-500 text-white px-8 py-3 rounded-[10px] hover:bg-action-600 transition-colors font-semibold text-lg"
            >
              {quizTaken === 0 ? 'Start Quiz' : 'Take Quiz Again (Final Attempt)'}
            </button>
          ) : (
            <div>
              <p className="text-error-500 font-semibold mb-4">
                You have no tries left for this book quiz.
              </p>
              <a
                href={`/books/quiz-results/${bookSlug}`}
                className="bg-neutral-400 text-white px-8 py-3 rounded-[10px] font-semibold text-lg inline-block"
              >
                View Your Results
              </a>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // Render processing progress or error states
  return (
    <div className="flex-1 bg-neutral-100 rounded-[15px] p-7 flex flex-col items-center justify-center gap-y-7">
      {error ? (
        <div className="text-center">
          <h3 className="text-[24px] font-bold text-error-500 mb-4">Oops!</h3>
          {/* Show specific message for "book too short" errors (matches booq-backend-nodejs/src/bookSummarizer/unifiedBookSummarizer.ts:76) */}
          <p className="text-neutral-600 mb-6">
            {errorMessage?.toLowerCase().includes('too short')
              ? errorMessage
              : 'Something went wrong. Please try searching again.'}
          </p>
          <button
            onClick={handleCancel}
            className="bg-action-500 text-white px-6 py-2 rounded-[10px] hover:bg-action-600 transition-colors"
          >
            Back to Search
          </button>
        </div>
      ) : (
        <>
          {/* Processing Progress */}
          <div className="text-center mb-4">
            <p className="text-lg font-semibold">{getProgressMessage()}</p>
            {currentPhase !== BooksPhaseOptions.processed && (
              <div className="flex justify-center mt-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-500"></div>
              </div>
            )}
          </div>

          {/* Detailed Progress Indicators */}
          {currentPhase && currentPhase !== BooksPhaseOptions.initial && (
            <div className="text-center">
              <h4 className="font-semibold mb-3">Progress</h4>
              <div className="flex flex-wrap gap-2 justify-center max-w-md">
                <div className={`px-3 py-1 rounded text-sm ${getPhaseStyle(progressFlags.search_completed, activePhase === 'search')}`}>
                  📖 Search
                </div>
                <div className={`px-3 py-1 rounded text-sm ${getPhaseStyle(progressFlags.metadata_downloaded, activePhase === 'metadata')}`}>
                  📝 Metadata
                </div>
                <div className={`px-3 py-1 rounded text-sm ${getPhaseStyle(progressFlags.cover_downloaded, activePhase === 'cover')}`}>
                  🖼️ Cover
                </div>
                <div className={`px-3 py-1 rounded text-sm ${getPhaseStyle(progressFlags.epub_downloaded, activePhase === 'epub')}`}>
                  📚 EPUB
                </div>
                <div className={`px-3 py-1 rounded text-sm ${getPhaseStyle(progressFlags.summarized, activePhase === 'summary')}`}>
                  📄 Summary
                </div>
                <div className={`px-3 py-1 rounded text-sm ${getPhaseStyle(progressFlags.questions_generated, activePhase === 'questions')}`}>
                  ❓ Questions
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleCancel}
            className="bg-neutral-300 text-neutral-700 px-6 py-2 rounded-[10px] hover:bg-neutral-400 transition-colors"
          >
            Cancel & Return to Search
          </button>
        </>
      )}
    </div>
  )
}

export default BookQuizContainer